import { readRtkQueryError } from "_shared/_errors/api-error";
import type Individual from "marketplace/individuals/individual";

/**
 * When a person's professional credentials count as filled out: the years of experience, licences,
 * registrations, bar admissions and degrees their profile lists in its one Credentials field.
 * Experience alone counts; nobody needs a registration to have something to write here.
 *
 * Nobody may be added to a team as a member until theirs are (#5800), and no profile is shown on the
 * Marketplace until they are (#6608). The API refuses the roster change and the publish; this is the
 * same rule, repeated so the profile form can say how far off someone is and the team pickers can
 * decline a person before the save is refused rather than after. It must match
 * `ProfessionalCredentials.MinimumLength` in `api/AttorneyHub/AttorneyHub.Domain/ProfessionalCredentials.cs`,
 * which is the one that decides.
 *
 * The field stays free text, so "filled out" is a length and not a structure. Ten turns away a
 * placeholder ("n/a", "TBD", "J.D." on its own) and lets through the shortest real answer, which for
 * some people is all there is: "20 years experience" is nineteen characters, and a minimum of twenty
 * refused it.
 */
export const credentialsMinimumLength = 10;

/**
 * What a picker says under a person it will not add, and why. One sentence for every tab, so the
 * reason reads the same wherever the person was found.
 */
export const credentialsMissingReason = "Can't join a team until their profile lists professional credentials";

export function areCredentialsFilledOut(credentials: string | undefined | null): boolean {
  return credentialsCharactersStillNeeded(credentials) === 0;
}

/** How many more characters the field needs before it counts, and zero once it does. */
export function credentialsCharactersStillNeeded(credentials: string | undefined | null): number {
  return Math.max(0, credentialsMinimumLength - (credentials?.trim().length ?? 0));
}

/** "6 more characters needed", or "1 more character needed"; empty once the field counts. */
export function credentialsCharactersNeededPhrase(credentials: string | undefined | null): string {
  const stillNeeded = credentialsCharactersStillNeeded(credentials);
  if (stillNeeded === 0) return "";
  return `${stillNeeded} more ${stillNeeded === 1 ? "character" : "characters"} needed`;
}

/**
 * Why a hidden profile cannot be shown on the Marketplace yet, or undefined once it can.
 *
 * A profile is published only once it lists credentials (#6608): the marketplace exists to find
 * people to staff teams, and a team will not take anybody without them. The API refuses the publish;
 * this says so before the person asks, with how far off they are.
 */
export function publishingBlockedReason(credentials: string | undefined | null): string | undefined {
  if (areCredentialsFilledOut(credentials)) return undefined;
  return (
    "Add professional credentials to show this profile on the Marketplace: " +
    `${credentialsCharactersNeededPhrase(credentials)}.`
  );
}

/**
 * Why a published profile's change cannot be saved, or undefined when it can: a profile shown on the
 * Marketplace keeps its credentials, and is hidden first to remove them (#6608).
 */
export function publishedCredentialsReason(credentials: string | undefined | null): string | undefined {
  if (areCredentialsFilledOut(credentials)) return undefined;
  return (
    "A profile shown on the Marketplace must list professional credentials " +
    `(${credentialsCharactersNeededPhrase(credentials)}). Hide the profile to remove them.`
  );
}

/**
 * What the API said about the credentials when it refused a profile save or publish for them, or
 * undefined when that is not why it refused (#6608). Its message is written for the profile's owner,
 * and is read only from the API's own problem details: a legacy body's text is never shown.
 */
export function credentialsRefusal(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null || !("status" in error)) return undefined;
  const result = readRtkQueryError(error as Parameters<typeof readRtkQueryError>[0]);
  if (!result.trusted || result.category !== "validation") return undefined;
  return result.fieldErrors.credentials?.[0];
}

/**
 * Whether this individual's record shows their credentials missing, rather than not showing them at
 * all. A contact-only record is the API withholding the profile from this viewer, so it says nothing
 * either way, and a picker should not refuse somebody it merely cannot see into: the API still decides
 * when the team is saved. (Somebody the viewer shares a proposal with arrives as a whole profile,
 * credentials included, published or not — #5766.)
 */
export function isKnownToLackCredentials(individual: Individual | undefined): boolean {
  if (!individual || individual.contactOnly) return false;
  return !areCredentialsFilledOut(individual.profile?.credentials);
}
